"use client";

import useOutsideClick from "@/app/hooks/useOutsideClick";
import FiltersContent from "@/app/search/facets/filtersContent";
import { CategoryFacetType } from "@/app/search/utils/createUsableCategoryFacet";
import type { AlgoliaQuery } from "@/declarations/algoliaQuery";
import { FilterType } from "@/types/search";
import CloseIcon from "public/next-public/common/icons/close-icon.svg";
import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import styles from "./filters.module.scss";

type FiltersPanelProps = {
  isOpen: boolean;
  onClose: any;
  filterType: FilterType;
  categoryResetSignal: number;
  setCategoryResetSignal: Dispatch<SetStateAction<number>>;
  facets: Record<string, Record<string, number>>;
  categoryFacets: CategoryFacetType[];
  categoryPageName?: string;
  isMobile?: boolean;
  setSelectedValues: Dispatch<SetStateAction<Record<string, Record<string, boolean>>>>;
  searchQuery: AlgoliaQuery;
};
const FiltersPanel: React.FC<FiltersPanelProps> = ({
  isOpen,
  filterType,
  onClose,
  categoryResetSignal,
  setCategoryResetSignal,
  facets,
  categoryPageName,
  categoryFacets,
  isMobile = false,
  setSelectedValues,
  searchQuery,
}) => {
  const panelRef = useRef<HTMLElement>(null);
  const wasOpen = useRef(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (isOpen) {
      wasOpen.current = true;
      setIsClosing(false);
    } else if (wasOpen.current) {
      setIsClosing(true);
      const timeout = setTimeout(() => setIsClosing(false), 300);
      return () => clearTimeout(timeout);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || typeof document === "undefined") {
      return;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  useOutsideClick(panelRef, () => onClose(filterType), isOpen);

  return (
    <>
      {isOpen && <div className={styles.backDrop} />}
      <aside
        ref={panelRef}
        className={`${styles.filterPanel} ${isOpen ? styles.open : ""} ${isClosing ? styles.closing : ""} d-lg-none overflow-scroll`}
      >
        <div className={styles.filterHeader}>
          <span className={styles.filterTitle}>Filter by</span>
          <button className={styles.closePanel} onClick={(evt) => onClose(filterType, evt)} aria-label="Close filters">
            <CloseIcon width={16} height={16} />
          </button>
        </div>
        <div className={styles.filterContent}>
          <FiltersContent
            categoryResetSignal={categoryResetSignal}
            setCategoryResetSignal={setCategoryResetSignal}
            facets={facets}
            categoryFacets={categoryFacets}
            categoryPageName={categoryPageName}
            isMobile={isMobile}
            filterType={filterType}
            setSelectedValues={setSelectedValues}
            searchQuery={searchQuery}
          />
        </div>
      </aside>
    </>
  );
};

export default FiltersPanel;
